import { useState, useEffect } from "react";
import { Mail, X } from "lucide-react";
import { useEmailVerification } from "@/hooks/useEmailVerification";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";

/**
 * A lightweight, dismissible reminder shown to signed-in users whose email
 * is not yet verified. It appears once per device (until dismissed) and never
 * blocks access to the app. Dismissal is remembered in localStorage.
 */
const EmailVerificationReminder = () => {
  const { user } = useAuth();
  const { isUnverified, isSending, resendVerification } = useEmailVerification();
  const { toast } = useToast();
  const [dismissed, setDismissed] = useState(true);

  const storageKey = user?.id ? `email-verify-reminder-dismissed:${user.id}` : null;

  useEffect(() => {
    if (!storageKey) return;
    setDismissed(localStorage.getItem(storageKey) === "true");
  }, [storageKey]);

  if (!isUnverified || dismissed) return null;

  const handleDismiss = () => {
    if (storageKey) localStorage.setItem(storageKey, "true");
    setDismissed(true);
  };

  const handleResend = async () => {
    const { error } = await resendVerification();
    if (error) {
      toast({ title: "Couldn't send email", description: error, variant: "destructive" });
    } else {
      toast({
        title: "Verification email sent",
        description: "Check your inbox to confirm your email address.",
        duration: 6000,
      });
      handleDismiss();
    }
  };

  return (
    <div className="mx-auto max-w-2xl px-4 pt-3">
      <div className="flex items-start gap-3 rounded-xl border border-border/60 bg-muted/40 px-4 py-3">
        <Mail className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
        <div className="flex-1 min-w-0">
          <p className="text-[13.5px] font-medium text-foreground">Verify your email</p>
          <p className="text-[12.5px] leading-snug text-muted-foreground">
            We sent you a verification link. You can keep using SoundCheck in the meantime.
          </p>
          <button
            type="button"
            onClick={handleResend}
            disabled={isSending}
            className="mt-2 text-[12.5px] font-medium text-primary hover:underline disabled:opacity-50"
          >
            {isSending ? "Sending…" : "Resend verification email"}
          </button>
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss"
          className="shrink-0 text-muted-foreground/70 hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default EmailVerificationReminder;
